/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import { Button, Divider, Popover } from '@arco-design/web-react';
import { IconMenu } from '@arco-design/web-react/icon';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import NetworkIndicator from '@/components/NetworkIndicator';
import SoundIcon from '@/components/BackgroundVideo/SoundIcon';
import { RootState } from '@/store';
import { useScene } from '@/lib/useCommon';
import { useBackgroundVideoSound } from '@/lib/useBackgroundVideoSound';
import { isAllModesPath } from '@/lib/interactionExperience';
import { useIsMobile } from '@/utils/utils';
import styles from './index.module.less';

const Disclaimer = 'https://www.volcengine.com/docs/6348/68916';
const ReversoContext = 'https://www.volcengine.com/docs/6348/68918';
const UserAgreement = 'https://www.volcengine.com/docs/6348/128955';
const OfficialSite = 'https://www.volcengine.com/product/veRTC/ConversationalAI';
const ContactUs = 'https://www.volcengine.com/contact/product?t=%E5%AF%B9%E8%AF%9D%E5%BC%8Fai&source=%E4%BA%A7%E5%93%81%E5%92%A8%E8%AF%A2';

interface HeaderProps {
  children?: React.ReactNode;
  hide?: boolean;
}

function Header(props: HeaderProps) {
  const { children, hide } = props;
  const isMobile = useIsMobile();
  const { interactionMode } = useSelector((state: RootState) => state.room);
  const { avatarBgVideoUrl } = useScene();
  const { pathname } = useLocation();
  const [bgSoundOn, setBgSoundOn] = useBackgroundVideoSound();
  const showSoundToggle =
    isAllModesPath(pathname) &&
    interactionMode === 'immersive' &&
    !isMobile &&
    Boolean(avatarBgVideoUrl);

  const MenuProps = [
    {
      name: '免责声明',
      url: Disclaimer,
    },
    {
      name: '隐私政策',
      url: ReversoContext,
    },
    {
      name: '用户协议',
      url: UserAgreement,
    },
    {
      name: '官网链接',
      url: OfficialSite,
    },
    {
      name: '联系我们',
      url: ContactUs,
    },
  ];

  return (
    <div
      className={styles.header}
      style={{
        display: hide ? 'none' : 'block',
      }}
    >
      <div className={`${styles.headerInner} ${isMobile ? styles.headerInnerMobile : ''}`}>
        <div className={`${styles['header-logo']} ${isMobile ? styles.headerLogoMobile : ''}`}>
          {isMobile ? null : (
            <Popover
              content={
                <div className={styles['menu-wrapper']}>
                  {MenuProps.map((menuItem) => (
                    <Button
                      type="text"
                      key={menuItem.name}
                      onClick={() => {
                        window.open(menuItem.url, '_blank');
                      }}
                    >
                      {menuItem.name}
                    </Button>
                  ))}
                </div>
              }
            >
              <IconMenu className={styles['header-setting-btn']} />
            </Popover>
          )}
          <div className={styles.brandMark} aria-label="Voxie">
            <span className={styles.brandWord}>Voxie</span>
            <span className={styles.brandBars} aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </span>
          </div>
          {isMobile ? null : <Divider type="vertical" className={styles.headerDivider} />}
          {isMobile ? null : <span className={styles['header-logo-text']}>AI 聊天新姿势</span>}
        </div>
        {isMobile ? null : children}
      </div>
      <div className={styles.headerStatus}>
        {showSoundToggle && (
          <button
            type="button"
            className={styles.bgSoundToggle}
            onClick={() => setBgSoundOn(!bgSoundOn)}
            aria-pressed={bgSoundOn}
            title={bgSoundOn ? '关闭背景音效' : '开启背景音效'}
            aria-label={bgSoundOn ? '关闭背景音效' : '开启背景音效'}
          >
            <SoundIcon on={bgSoundOn} size={14} />
          </button>
        )}
        <NetworkIndicator />
      </div>
    </div>
  );
}

export default Header;
